import {
  BarChart3,
  CheckCircle,
  Package,
  RotateCcw,
  Trash2
} from 'lucide-react'
import { useEffect, useState } from 'react'

import { useDevanningState } from '../stores/devanningStore'
import { usePalletActions, usePalletState } from '../stores/palletStore'
import {
  useScanProgressActions,
  useScanProgressState
} from '../stores/scanProgressStore'

import MixedPallet from './pallets/MixedPallet'

import ConfirmDialog from '@/components/common/ConfirmDialog'
import { inboundV2Service } from '@/services'
import { useUIStore } from '@/stores/useUIStore'

const ScanProgress: React.FC = () => {
  const [visible, setVisible] = useState<boolean>(false)
  const { progress } = useScanProgressState()
  const { fetchProgress } = useScanProgressActions()
  const { showError, showSuccess } = useUIStore()
  const { masterBill } = useDevanningState()

  // const { loading } = usePalletState()
  const { quickVisible } = usePalletState()
  const { showQuickConfirm } = usePalletActions()

  // 清除当前分货已扫箱号
  const handleClearScannedCargo = async () => {
    if (!masterBill?.id) {
      showError('没有选择主提单')
      return
    }
    if (!progress?.markId) {
      showError('当前没有分货编码')
      return
    }
    const { success, message, data } =
      await inboundV2Service.clearScannedByMarkId(
        masterBill.id,
        progress.markId
      )
    if (success) {
      showSuccess(message)
      setVisible(false)
      fetchProgress(data?.markId)
    } else {
      showError(message)
    }
  }

  useEffect(() => {
    fetchProgress()
  }, [])

  return (
    <div className="px-6 pb-6 h-full overflow-y-auto">
      <ConfirmDialog
        title="清空已扫描箱货"
        isOpen={visible}
        onConfirm={() => handleClearScannedCargo()}
        onCancel={() => setVisible(false)}
      >
        确定要清空所有已扫描的箱货吗？此操作不可撤销。
      </ConfirmDialog>
      {progress ? (
        <div className="space-y-6">
          {/* Marks 信息和操作区 */}
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg p-4">
            <div className="flex-1 text-2xl font-bold text-gray-900 bg-white px-4 py-3 rounded-lg shadow-sm text-center">
              {progress.markNumber}
            </div>
          </div>

          {/* 进度展示区域 - 左右布局 */}
          <div className="flex items-start gap-6">
            {/* 左侧进度环区域 */}
            <div className="flex-shrink-0 text-center">
              <div className="relative w-32 h-32 mx-auto mb-4">
                <svg
                  className="w-32 h-32 transform -rotate-90"
                  viewBox="0 0 36 36"
                >
                  {/* 外圈背景圆环 */}
                  <path
                    className="text-gray-200"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                  />
                  {/* 外圈 - 已扫描进度（蓝色） */}
                  <path
                    className="text-blue-400"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeDasharray={`${
                      ((progress.summary.scanned +
                        progress.summary.palletized) /
                        progress.summary.total) *
                      100
                    }, 100`}
                    strokeLinecap="round"
                  />
                </svg>
                {/* 中心文字 */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="text-lg font-bold text-gray-900">
                    {Math.round(
                      ((progress.summary.scanned +
                        progress.summary.palletized) /
                        progress.summary.total) *
                        100
                    )}
                    %
                  </div>
                  <div className="text-xs text-gray-500">
                    {progress.summary.scanned + progress.summary.palletized}/
                    {progress.summary.total}
                  </div>
                </div>
              </div>

              {/* 状态显示 */}
              <div
                className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
                  progress.summary.palletized === progress.summary.total
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : progress.summary.scanned === progress.summary.total
                    ? 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                    : 'bg-blue-100 text-blue-700 border border-blue-300'
                }`}
              >
                {progress.summary.palletized === progress.summary.total ? (
                  <CheckCircle className="w-5 h-5 mr-2" />
                ) : progress.summary.scanned === progress.summary.total ? (
                  <Package className="w-5 h-5 mr-2" />
                ) : (
                  <RotateCcw className="w-5 h-5 mr-2" />
                )}
                {progress.summary.palletized === progress.summary.total
                  ? '打托完成'
                  : progress.summary.scanned === progress.summary.total
                  ? '扫描完成'
                  : '进行中'}
              </div>
            </div>

            {/* 右侧统计数据 */}
            <div className="flex-1 space-y-4">
              {/* 统计卡片 - 混合布局 */}
              <div className="flex gap-4">
                {/* 左侧：已扫描和待扫描 */}
                <div className="flex-1 space-y-3">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {progress.summary.scanned}
                    </div>
                    <div className="text-sm text-blue-600 flex items-center justify-center">
                      已扫描
                      {progress.summary.scanned >= 0 && (
                        <button
                          className="ml-2"
                          onClick={() => setVisible(true)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-gray-600">
                      {progress.summary.total -
                        progress.summary.scanned -
                        progress.summary.palletized}
                    </div>
                    <div className="text-sm text-gray-600">待扫描</div>
                  </div>
                </div>

                {/* 右侧：已打托 */}
                <div className="flex-1">
                  <div className="bg-green-50 rounded-lg p-4 text-center h-full flex flex-col justify-center">
                    <div className="text-2xl font-bold text-green-600">
                      {progress.summary.palletized}
                    </div>
                    <div className="text-sm text-green-600">已打托</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 打托操作区域 */}
          {progress.summary.scanned > 0 && (
            <div className="pt-4 border-t border-gray-200">
              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900 flex items-center space-x-2">
                      <Package className="w-5 h-5" />
                      <span>可打托货物</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      已扫描 {progress.summary.scanned} 件货物可进行打托
                    </div>
                  </div>
                  <button
                    onClick={() => showQuickConfirm()}
                    disabled={quickVisible}
                    className="px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-md hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {quickVisible ? '打托中...' : '🚛 一键打托'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 混合打托操作区域 */}
          <MixedPallet />
        </div>
      ) : (
        <div className="text-center py-12">
          <BarChart3 className="w-24 h-24 text-gray-300 mx-auto mb-4" />
          <div className="text-gray-500 text-lg">暂无扫描进度</div>
          <div className="text-gray-400 text-sm mt-2">请先扫描货物条码</div>
        </div>
      )}
    </div>
  )
}

export default ScanProgress
